import { useState } from 'react'
import { WorkshopSection } from './WorkshopSection'
import { Button } from '@/components/ui/Button'
import { useWorkshopMode } from './WorkshopModeContext'
import { PowerEditorDrawer } from './PowerEditorDrawer'
import { useFeature } from '@/hooks'

interface Props {
  /** Open the PersonaEditor modal scoped to a tab (§4.5). */
  onLaunchModal: (tab: string) => void
  /** Open the ComposedPromptInspector modal. */
  onViewComposedPrompt: () => void
}

export function AdvancedSection({ onLaunchModal, onViewComposedPrompt }: Props) {
  const { mode } = useWorkshopMode()
  const [powerOpen, setPowerOpen] = useState(false)
  const showTools = useFeature('tool_framework')

  const infraLinks = [
    { tab: 'mode', label: 'Voice & avatar' },
    { tab: 'rag', label: 'RAG server' },
    { tab: 'channels', label: 'Channels' },
    ...(showTools ? [{ tab: 'tools', label: 'Tools' }] : []),
    { tab: 'payments', label: 'Payments' },
  ]

  return (
    <WorkshopSection emoji="⚙️" title="Advanced" subtitle="Composed prompt, connections, channels, payments.">
      <div className="space-y-5">
        <div>
          <span className="mb-1 block text-sm font-medium">Composed prompt</span>
          <p className="mb-2 text-xs text-muted-foreground">
            The full prompt this persona sends, section by section — including the
            layers added automatically during a conversation.
          </p>
          <Button variant="secondary" size="sm" onClick={onViewComposedPrompt}>
            View composed prompt →
          </Button>
        </div>

        <div>
          <span className="mb-2 block text-sm font-medium">Infra &amp; channels</span>
          <div className="flex flex-wrap gap-2">
            {infraLinks.map((i) => (
              <Button key={i.tab} variant="secondary" size="sm" onClick={() => onLaunchModal(i.tab)}>
                {i.label} →
              </Button>
            ))}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">Opens the full editor for these settings.</p>
        </div>

        {mode === 'advanced' && (
          <div>
            <span className="mb-1 block text-sm font-medium">Raw bundle JSON</span>
            <p className="mb-2 text-xs text-muted-foreground">
              Hand-edit the entire bundle as JSON. Apply stages the result as an
              unsaved draft — Save in the top bar commits it.
            </p>
            <Button variant="secondary" size="sm" onClick={() => setPowerOpen(true)}>
              Raw bundle JSON →
            </Button>
          </div>
        )}
      </div>
      <PowerEditorDrawer open={powerOpen} onOpenChange={setPowerOpen} />
    </WorkshopSection>
  )
}
